import {
  createContext,
  type ReactNode,
  useCallback,
  useContext,
  useMemo,
  useState,
} from 'react';

import {
  addLine,
  type CartLine,
  clearCart as clearStoredCart,
  loadCart,
  removeLine,
  saveCart,
} from './cart';

interface CartContextValue {
  lines: CartLine[];
  add: (line: CartLine) => void;
  remove: (id: string) => void;
  clear: () => void;
}

const CartContext = createContext<CartContextValue | null>(null);

/**
 * Holds the cart state for the whole SPA, persisted to localStorage (so it
 * survives the PKCE login redirect). All list logic lives in the pure
 * `cart.ts` module; this provider only owns the React state + persistence.
 */
export function CartProvider({ children }: { children: ReactNode }) {
  const [lines, setLines] = useState<CartLine[]>(() => loadCart());

  const add = useCallback((line: CartLine) => {
    setLines(prev => {
      const next = addLine(prev, line);
      saveCart(next);
      return next;
    });
  }, []);

  const remove = useCallback((id: string) => {
    setLines(prev => {
      const next = removeLine(prev, id);
      saveCart(next);
      return next;
    });
  }, []);

  const clear = useCallback(() => {
    clearStoredCart();
    setLines([]);
  }, []);

  // Stable context value (the callbacks never change identity): consumers
  // re-render only when the cart lines actually change.
  const value = useMemo(
    () => ({ lines, add, remove, clear }),
    [lines, add, remove, clear]
  );

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
}

export function useCart(): CartContextValue {
  const ctx = useContext(CartContext);
  if (!ctx) {
    throw new Error('useCart must be used within a CartProvider');
  }
  return ctx;
}
